// external imports
import React, { useContext, useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import Skeleton from 'react-loading-skeleton';
import { Modal } from 'bootstrap';
import PropTypes from 'prop-types';

// local imports (components, helper funcs)
import exportConfig from '../../config';
import Barplot from '../plots/Barplot';
import HotspotsProfile from '../plots/HotspotsProfile';
import PixelCountPlot from '../plots/PixelCountPlot';
import QCingFormSmelter from '../forms/QCingFormSmelter';
import QCingFormStockpiles from '../forms/QCingFormStockpiles';
import CompareImageModal from '../modals/CompareImageModal';
import StockpilesGeojsonsList from '../additional_comps/StockpilesGeojsonsList';
import { QCImageSmelter } from '../maps/QCImageSmelter';
import { QCImageStockpiles } from '../maps/QCImageStockpiles';
import sentinelHubLogo from '../../images/SHlogo.svg';
import { getSentinelHubUrl } from '../../helpers/leafletHelpers';
import formatDate from '../../helpers/formatDate';

// custom hooks & context
import { pipelineContext } from '../../contexts/pipelineContext';
import useGainsQCApi from '../../services/useGainsQCApi';

const QCing = ({
	currentCapture,
	currentCaptureIndex,
	qcState,
	setQCState,
	onQCSubmit,
	onStockpilesQCSubmit,
	setDisagreeReason,
	disagreeReason,
	stockpilesFormValue,
	setStockpilesFormValue,
	loading,
	setLoading,
	postCaptureStateSmelters,
	user
}) => {
	const pipelineContextQCing = useContext(pipelineContext);
	const stockpilesCase = 2;

	const [modalOpen, setModalOpen] = useState(false);
	const [selectedRadio, setSelectedRadio] = useState(1);
	const [userSelectedQCState, setUserSelectedQCState] = useState(false);
	const [sentinelUrl, setSentinelUrl] = useState('#');
	const [drawnItemsValid, setDrawnItemsValid] = useState(true);
	const [stockpilesGeojsons, setStockpilesGeojsons] = useState();
	const [activeStockpile, setActiveStockpile] = useState(null);
	const [selectedMapView, setSelectedMapView] = useState(null);
	const [compareImageIndex, setCompareImageIndex] = useState(null);
	const [compareImageModalOpen, setCompareImageModalOpen] = useState(false);

	// all EP data fetched on this page
	const imageData = useGainsQCApi();
	const historicPlotData = useGainsQCApi();
	const compareImageData = useGainsQCApi();
	const fetchImageComparisonList = useGainsQCApi();
	const hotspotsProfileData = useGainsQCApi();
	const pixelPlotData = useGainsQCApi();

	const sentinelUrl2 =
		imageData?.data?.data && currentCapture
			? getSentinelHubUrl(imageData.data.data, currentCapture)
			: '#';

	// TODO: check this
	// useEffect(() => {
	// 	const modalEl = document.getElementById('compareImageModal');
	// 	//console.log(modalEl);
	// 	// if (modalEl) {
	// 	// 	modalEl.addEventListener('shown.bs.modal', handleModalShown);
	// 	// }
	// }, []);

	// console.log('Selected radio', selectedRadio);
	// console.log('Current capture', currentCaptureIndex, currentCapture);

	const fetchImageData = async () => {
		/**
		 * fetch the capture data
		 * returns aoi, perm regions, hotspots, bbox
		 */

		if (pipelineContextQCing.comm === 'stockpiles') {
			await imageData.makeRequest(
				`/${pipelineContextQCing.comm}/capture_image?stacItemId=${currentCapture.stac_item_id}&sensor=${currentCapture.stac_collection}&siteId=${currentCapture.site_id}&siteName=${currentCapture.name}`,
				'GET',
				null,
				`capture ${currentCapture.id}`
			);
		} else if (pipelineContextQCing.comm === 'smelters') {
			await imageData.makeRequest(
				`/${pipelineContextQCing.comm}/capture_image?stacItemId=${currentCapture.stac_item_id}&sensor=${currentCapture.sensor}&siteId=${currentCapture.site_id}`,
				'GET',
				null,
				pipelineContextQCing.notificationsEnabled
					? (currentCaptureIndex = 0 ? 'first capture..' : 'next capture..')
					: null
			);
		}
	};

	const fetchImageComparisonData = async (cap) => {
		/**
		 * fetch the current comparison image data for image comparison modal
		 * returns aoi, perm regions, hotspots, bbox
		 */

		console.log('fetching comparison image data');

		if (cap?.stac_item_id && cap?.sensor) {
			await compareImageData.makeRequest(
				`/${pipelineContextQCing.comm}/capture_image?stacItemId=${cap.stac_item_id}&sensor=${cap.sensor}&siteId=${currentCapture.site_id}`,
				'GET',
				null
				//'fetching comparison image'
			);
		}
	};

	const fetchHotspotsData = async () => {
		/**
		 * fetches data for hotspots pixel plot
		 */

		await hotspotsProfileData.makeRequest(
			`/${pipelineContextQCing.comm}/hotspot_profile?sensor=${currentCapture.sensor}&stacItemId=${currentCapture.stac_item_id}&site_id=${currentCapture.site_id}`,
			'GET',
			null,
			''
		);
	};

	const fetchBarplotData = async () => {
		/**
		 * fetches data for historic bar plot
		 */

		if (!currentCapture) return;

		let maxCaptureDate = new Date(currentCapture.captured);
		maxCaptureDate.setDate(maxCaptureDate.getDate() + 14);

		await historicPlotData.makeRequest(
			`/${pipelineContextQCing.comm}/barplot?siteId=${
				currentCapture.site_id
			}&date=${maxCaptureDate.toISOString().split('T')[0]}&rawData=true`,
			'GET',
			null,
			''
		);
	};

	const fetchComparingImagesList = async () => {
		/**
		 * fetches list of captures before/after the current capture
		 * to create a queue to compare to current capture
		 */

		await fetchImageComparisonList.makeRequest(
			`/${pipelineContextQCing.comm}/previous_captures?captureId=${currentCapture.id}&table=${currentCapture.table_name}`,
			'GET',
			null,
			''
		);
	};

	function findNearestPreviousIndex(date, list) {
		if (!Array.isArray(list)) {
			console.error('Expected an array, got:', list);
			return -1;
		}

		const targetDate = new Date(date).getTime();

		return list.reduce((nearestIndex, obj, index) => {
			const objDate = new Date(obj.captured).getTime();

			if (objDate < targetDate) {
				const nearestDate = new Date(
					list[nearestIndex]?.captured || 0
				).getTime();
				if (nearestDate < objDate || nearestIndex === -1) {
					return index;
				}
			}

			return nearestIndex;
		}, -1);
	}

	useEffect(() => {
		/**
		 * on mount ie each capture change, fetch image data, reset forms
		 * on unmount, remove EP data
		 */

		setUserSelectedQCState(false);
		setStockpilesFormValue(1);
		setStockpilesGeojsons(null);
		fetchImageData();

		// if cap is being RE QCd
		if (
			currentCapture.state &&
			currentCapture.qc_user &&
			currentCapture.qc_state === 1
		) {
			currentCapture.reQCing = true;
		}

		if (fetchImageComparisonList.data && fetchImageComparisonList?.data?.data) {
			const index = findNearestPreviousIndex(
				currentCapture.captured,
				fetchImageComparisonList.data.data
			);
			setCompareImageIndex(index);
		}

		return () => {
			imageData.resetData();
			historicPlotData.resetData();
			hotspotsProfileData.resetData();
			pixelPlotData.resetData();
		};
	}, [currentCapture]);

	function findPreviousIndex(list, targetDate) {
		let previousIndex = -1;
		let closestTime = -Infinity; // Track the closest previous time

		list.forEach((obj, index) => {
			const objDate = new Date(obj.captured).getTime();
			if (objDate < targetDate.getTime() && objDate > closestTime) {
				closestTime = objDate;
				previousIndex = index;
			}
		});

		return previousIndex;
	}

	useEffect(() => {
		if (
			fetchImageComparisonList.data &&
			fetchImageComparisonList.data?.data &&
			!fetchImageComparisonList.error
		) {
			let date = new Date(currentCapture.captured);
			const previousIndex = findPreviousIndex(
				fetchImageComparisonList.data.data,
				date
			);
			setCompareImageIndex(previousIndex);
		}
	}, [fetchImageComparisonList.data, fetchImageComparisonList.error]);

	useEffect(() => {
		if (fetchImageComparisonList?.data?.data[compareImageIndex]) {
			fetchImageComparisonData(
				fetchImageComparisonList.data.data[compareImageIndex]
			);
		}
	}, [compareImageIndex, fetchImageComparisonList.data]);

	useEffect(() => {
		/**
		 * once imageData has been fetched for capture
		 * fetch additional data (historic plot, hotspots profile, pixel plot)
		 */

		console.log('Use effect imageData');

		if (imageData.data) {
			//console.log("there is image data")
			setLoading(false);
			if (pipelineContextQCing.comm === 'smelters') {
				//console.log("there is image data - smelters")
				fetchBarplotData();
				fetchComparingImagesList();

				if (currentCapture.hotspots > 0) {
					fetchHotspotsData();
				} else {
					hotspotsProfileData.overrideData([]);
				}

				if (imageData.data.hasOwnProperty('data')) {
					setSentinelUrl(
						getSentinelHubUrl(imageData.data.data, currentCapture)
					);
				}

				// if/not capture has already been QC'd
				if (currentCapture) {
					//console.log("there is a current capture")
					if (currentCapture.hasOwnProperty('newQC')) {
						//console.log("newQC", currentCapture.newQC)
						setSelectedRadio(currentCapture.newQC);
						setQCState(currentCapture.newQC);
					} else if (currentCapture.reQCing) {
						//console.log("qc_state", currentCapture.qc_state)
						setSelectedRadio(currentCapture.state);
						setQCState(currentCapture.state);
					} else {
						//console.log("initial_state", currentCapture.initial_state)
						setSelectedRadio(currentCapture.initial_state);
						setQCState(currentCapture.initial_state);
					}
				} else {
					console.error('No current capture!');
				}
			} else if (pipelineContextQCing.comm === 'stockpiles') {
				pixelPlotData.makeRequest(
					`/${pipelineContextQCing.comm}/pixel_count?siteId=${currentCapture.site_id}`,
					'GET',
					null,
					'pixel plot data'
				);

				// setStockpilesGeojsons({
				// 	0: {
				// 		original: imageData.data.data.hasOwnProperty('stockpiles')
				// 			? JSON.parse(imageData.data.data.stockpiles).features
				// 			: [],
				// 		edited: [],
				// 		deleted: [],
				// 		created: [],
				// 		simplified: []
				// 	}
				// });
				setStockpilesGeojsons([
					imageData.data.data.hasOwnProperty('stockpiles')
						? JSON.parse(imageData.data.data.stockpiles).features
						: []
				]);
			}
		} else {
			console.error('No image data');
		}
	}, [imageData.data]);

	const userHasSelected = (selectedState) => {
		setUserSelectedQCState(true);
		setQCState(selectedState);
		setSelectedRadio(selectedState);
	};

	const skipCapture = (e) => {
		/**
		 * if user skips capture, move to next
		 * if capture is last capture, jump to first capture not QC'd
		 */

		console.log('skip capture');

		e.preventDefault();

		if (
			pipelineContextQCing.currentCaptureIndex !==
			pipelineContextQCing.capsInPipeline.length - 1
		) {
			pipelineContextQCing.setCurrentCaptureIndex(
				pipelineContextQCing.currentCaptureIndex + 1
			);
		} else {
			pipelineContextQCing.setCurrentCaptureIndex(0);
		}
	};

	const backCapture = (e) => {
		/**
		 * if user skips capture, move to next
		 * if capture is last capture, jump to first capture not QC'd
		 */

		e.preventDefault();

		pipelineContextQCing.setCurrentCaptureIndex(
			pipelineContextQCing.currentCaptureIndex - 1
		);
	};

	const copyAOIToClipboard = () => {
		if (
			imageData.data &&
			imageData.data.hasOwnProperty('data') &&
			imageData.data.data.hasOwnProperty('aoi')
		) {
			const text = imageData.data.data.aoi;
			navigator.clipboard
				.writeText(text)
				.then(() => {
					alert('Copied AOI to clipboard');
				})
				.catch((err) => {
					alert('Couldnt copy AOI to clipboard');
				});
		}
	};

	const [isOpen, setIsOpen] = useState(false);

	const checkModalOpen = () => {
		//console.log('opening/closing modal');
		const modalEl = document.getElementById('compareImageModal');
		if (!modalEl) return;

		const handleOpen = () => setIsOpen(true);
		const handleClose = () => setIsOpen(false);

		modalEl.addEventListener('shown.bs.modal', handleOpen);
		modalEl.addEventListener('hidden.bs.modal', handleClose);

		return () => {
			modalEl.removeEventListener('shown.bs.modal', handleOpen);
			modalEl.removeEventListener('hidden.bs.modal', handleClose);
		};
	};

	// console.log('Caps in pipeline QCing', pipelineContextQCing.capsInPipeline)
	// console.log('Current cap QCing', currentCapture)
	// console.log("sentinelUrl", sentinelUrl)
	console.log(
		currentCapture,
		imageData.data,
		fetchImageComparisonList.data?.data,
		compareImageData?.data
	);

	if (pipelineContextQCing.comm === 'smelters') {
		return (
			<>
				<div className="px-1 mx-1 mx-md-3" style={{ cursor: 'default' }}>
					<hr></hr>

					{currentCapture && imageData.data && (
						<>
							{fetchImageComparisonList.data?.data &&
								compareImageData?.data && (
									<CompareImageModal
										imageData={imageData.data}
										modalId={'compareImageModal'}
										compareImageData={
											compareImageData?.data ? compareImageData.data : null
										}
										modalIsOpen={isOpen}
										loading={compareImageData.loading}
										currentCap={currentCapture}
										compareImageIndex={compareImageIndex}
										setCompareImageIndex={setCompareImageIndex}
										compareImageDataList={
											fetchImageComparisonList?.data?.data
												? fetchImageComparisonList.data.data
												: null
										}
										compareImageModalOpen={compareImageModalOpen}
										setCompareImageModalOpen={setCompareImageModalOpen}
									/>
								)}
						</>
					)}

					<div id="viewer" />

					<div className="row">
						<div className="col-12 col-md-6 order-md-1 d-flex justify-content-start align-items-center">
							{/* <button
								className="btn interactButtonSecondary backButton"
								//onClick={handleBackButton}
								style={{ width: '200px' }}
							>
								<i class="fa-solid fa-left-long pe-3"></i>
								Change QC type
							</button> */}
							<h1 className="mt-2">QC</h1>

							{pipelineContextQCing.qcType === 'Re-QC' ? (
								<h4 className="ms-4 mt-2">Re-QC for capture</h4>
							) : (
								<h4 className="ms-4 mt-2">Perform QC for capture</h4>
							)}
						</div>
						<div className="col-12 "></div>
						<div className="col-12 col-md-6 order-md-2 d-flex flex-column justify-content-center align-items-end">
							{imageData.error && (
								<>
									<h5 className="text-warning">
										<i className="fa-solid fa-triangle-exclamation"></i> Error
										fetching image
									</h5>
									<p className="text-warning py-0 my-0">
										You can try to{' '}
										<span
											className="fw-bold"
											onClick={() => fetchImageData()}
											style={{ cursor: 'pointer' }}
										>
											re-fetch the image{' '}
											<i
												id="captureImageHover"
												className="fa-solid fa-arrows-rotate ms-1"
											></i>
										</span>
										, skip the capture{' '}
										<button
											className="btn btn-sm interactButton"
											onClick={(e) => skipCapture(e)}
										>
											Skip{' '}
										</button>{' '}
									</p>
								</>
							)}
							{historicPlotData.error && (
								<h5 className="text-warning">
									<i className="fa-solid fa-triangle-exclamation"></i> Error
									fetching historic data
								</h5>
							)}
							{hotspotsProfileData.error && (
								<h5 className="text-warning">
									<i className="fa-solid fa-triangle-exclamation"></i> Error
									fetching hotspots data
								</h5>
							)}
							{postCaptureStateSmelters.error &&
								currentCapture &&
								currentCapture.hasOwnProperty('error') && (
									<>
										<h5 className="text-warning">
											<i
												className="fa-solid fa-triangle-exclamation"
												data-tooltip-id="posting-cap-error"
											></i>{' '}
											Error QCing capture: skip to next capture
										</h5>

										<Tooltip id="posting-cap-error" place="top" effect="solid">
											{postCaptureStateSmelters.error?.message}
										</Tooltip>
									</>
								)}
						</div>
					</div>

					{/* SITE/CAPTURE INFO */}
					<div
						className="row px-0 px-lg-2 pt-2"
						id="QCIng-info-row"
						style={{ minHeight: '5vh' }}
					>
						<div className="col-12 col-md-6 col-xl-3 d-flex justify-content-center justify-content-md-start">
							<p className="py-0 my-0" data-tooltip-id="QCingSiteInfo">
								<i class="fa-solid fa-location-dot me-2"></i>
								{!currentCapture ? (
									<Skeleton width={80} />
								) : currentCapture.site_name.length < 30 ? (
									`${currentCapture.site_id} - ${currentCapture.site_name}`
								) : (
									`${currentCapture.site_id} - ${currentCapture.site_name.slice(
										0,
										30
									)}...`
								)}
							</p>

	
							<Tooltip id="QCingSiteInfo" place="top" effect="solid">
								Site id - Site name <br />
								{currentCapture && currentCapture.site_name}
							</Tooltip>
						</div>
						<div className="col-12 col-md-6 col-xl-3 d-flex justify-content-center justify-content-md-end justify-content-xl-center">
							<div className="row">
								<div className="d-flex justify-content-end">
									<p
										className="text-capitalize ps-md-4 py-0 my-0"
										data-tooltip-id="QCingCaptureInfo"
									>
										<i className="fa-regular fa-image  me-2"></i>
										{!currentCapture ? (
											<Skeleton width={80} />
										) : (
											`${currentCapture.id} - ${currentCapture.sensor}`
										)}
									</p>
									<Tooltip id="QCingCaptureInfo" place="top" effect="solid">
										Capture id - Sensor
									</Tooltip>
								</div>
							</div>
						</div>
						<div className="col-12 col-md-6 col-xl-3 d-flex justify-content-center justify-content-md-start justify-content-xl-center">
							<div className="row">
								<div className="col d-flex justify-content-end">
									<p
										className="text-capitalize ps-lg-4 py-0 my-0"
										data-tooltip-id="QCingCaptureDate"
										data-bs-toggle={
											currentCapture &&
											imageData.data &&
											fetchImageComparisonList.data?.data &&
											compareImageData?.data
												? 'modal'
												: null
										}
										data-bs-target={
											currentCapture &&
											imageData.data &&
											fetchImageComparisonList.data?.data &&
											compareImageData?.data
												? '#compareImageModal'
												: null
										}
										onClick={(e) => {
											console.log('compareImageModal onclick');
											setTimeout(() => {
												setCompareImageModalOpen(true);
											}, 500);

											checkModalOpen();
										}}
									>
										<i class="fa-solid fa-calendar-day me-2"></i>
										{!currentCapture ? (
											<Skeleton width={80} />
										) : (
											`${
												currentCapture.captured
													? formatDate(currentCapture.captured.slice(0, 10))
													: '-'
											}`
										)}
									</p>

									{sentinelUrl === '#' || sentinelUrl === undefined ? (
										<a className="ms-2">
											<img
												src={sentinelHubLogo}
												alt=""
												width="25"
												height="25"
												className="rounded-circle ms-1 me-3"
												data-tooltip-id="link-to-sentinel-hub-disabled"
											></img>
										</a>
									) : (
										<a
											href={sentinelUrl2}
											target="_blank"
											rel="noreferrer"
											className="ms-2 mb-1"
										>
											<img
												src={sentinelHubLogo}
												alt=""
												width="25"
												height="25"
												className="rounded-circle ms-1 me-3"
												id="sentinel-hub-icon"
												data-tooltip-id="link-to-sentinel-hub"
											></img>
										</a>
									)}

									<p>
										<i
											className="fa-solid fa-draw-polygon fa-md"
											data-tooltip-id="copyAOIToClipboard"
											onClick={
												currentCapture && imageData.data
													? () => copyAOIToClipboard()
													: null
											}
										></i>
									</p>

									<Tooltip id="copyAOIToClipboard" place="top" effect="solid">
										Click to copy AOI to clipboard
									</Tooltip>
									<Tooltip
										id="QCingCaptureDate"
										place="top"
										effect="solid"
										style={{ zIndex: 99999 }}
									>
										Capture date <br></br>(Click to compare images from
										different dates)
									</Tooltip>
									<Tooltip id="link-to-sentinel-hub" place="top" effect="solid">
										View capture on Sentinel Hub
									</Tooltip>
									<Tooltip
										id="link-to-sentinel-hub-disabled"
										place="top"
										effect="solid"
									>
										Link to Sentinel Hub not available until image data is
										fetched successfully
									</Tooltip>
								</div>
							</div>
						</div>
						<div className="col-12 col-md-6 col-xl-3 d-flex justify-content-center justify-content-md-end justify-content-xl-end">
							<p
								className="mx-3 py-0 my-0"
								data-tooltip-id="QCingCloudFracInfo"
							>
								<i class="fa-solid fa-cloud pe-1"></i>
								{!currentCapture ? (
									<Skeleton width={30} />
								) : currentCapture.cloud_frac == null ? (
									'-'
								) : currentCapture.cloud_frac === 0 ? (
									0
								) : (
									currentCapture.cloud_frac.toFixed(2)
								)}
							</p>
							<Tooltip id="QCingCloudFracInfo" place="top" effect="solid">
								{currentCapture &&
									`Cloud fraction - ${
										currentCapture.cloud_frac == null
											? '-'
											: currentCapture.cloud_frac === 0
												? 0
												: currentCapture.cloud_frac.toFixed(2)
									}`}
							</Tooltip>

							<p className="mx-3 py-0 my-0" data-tooltip-id="QCingNoHotspots">
								<i class="fa-solid fa-fire pe-1"></i>
								{!currentCapture ? (
									<Skeleton width={30} />
								) : (
									currentCapture.hotspots
								)}
							</p>
							<Tooltip id="QCingNoHotspots" place="top" effect="solid">
								{currentCapture &&
									`Number of hotspots - ${currentCapture.hotspots}`}
							</Tooltip>

							<p
								className="mx-3 py-0 my-0"
								data-tooltip-id="QCingValidDataFracInfo"
							>
								<i class="fa-solid fa-chart-line pe-1"></i>
								{!currentCapture ? (
									<Skeleton width={30} />
								) : currentCapture.invalid_data_frac == null ? (
									'-'
								) : currentCapture.invalid_data_frac === 0 ? (
									0
								) : (
									currentCapture.invalid_data_frac.toFixed(2)
								)}
							</p>
							<Tooltip id="QCingValidDataFracInfo" place="top" effect="solid">
								{currentCapture &&
									`Invalid data fraction - ${
										currentCapture.invalid_data_frac == null
											? '-'
											: currentCapture.invalid_data_frac === 0
												? 0
												: currentCapture.invalid_data_frac.toFixed(2)
									}`}
							</Tooltip>

							<p className="mx-3 py-0 my-0" data-tooltip-id="QCingMBRInfo">
								<i class="fa-solid fa-signal pe-1"></i>
								{!currentCapture ? (
									<Skeleton width={30} />
								) : currentCapture.max_band_ratio == null ? (
									'-'
								) : currentCapture.max_band_ratio === 0 ? (
									0
								) : (
									currentCapture.max_band_ratio.toFixed(2)
								)}
							</p>
							<Tooltip id="QCingMBRInfo" place="top" effect="solid">
								{currentCapture &&
									`Max band ratio - ${
										currentCapture.max_band_ratio == null
											? '-'
											: currentCapture.max_band_ratio === 0
												? 0
												: currentCapture.max_band_ratio.toFixed(2)
									}`}
							</Tooltip>

							{!currentCapture ? (
								<>
									<p className="mx-3 py-0 my-0">
										<i class="fa-solid fa-volcano"></i> <Skeleton width={30} />
									</p>
									<Tooltip id="QCingUseSmokeYes" place="top" effect="solid">
										Use smoke (yes)
									</Tooltip>
								</>
							) : (
								<>
									<p className="mx-3">
										{currentCapture.use_smoke === 1 ? (
											<i
												class="fa-solid fa-volcano"
												data-tooltip-id="QCingUseSmokeYes"
											></i>
										) : (
											<div style={{ position: 'relative' }}>
												<div style={{ position: 'absolute' }}>
													<i
														class="fa-solid fa-volcano"
														data-tooltip-id="QCingUseSmokeNo"
													></i>
												</div>
												<div style={{ position: 'absolute' }}>
													<i
														class="fa-solid fa-slash"
														data-tooltip-id="QCingUseSmokeNo"
													></i>
												</div>
											</div>
										)}
									</p>
									<Tooltip id="QCingUseSmokeNo" place="top" effect="solid">
										Use smoke (no)
									</Tooltip>
								</>
							)}
						</div>
					</div>

					{/* IMAGES */}
					<div
						className="row mt-2"
						id="QCing-images-row"
						style={{ minHeight: '40vh' }}
					>
						<div className="col-12 my-2 my-lg-0 col-lg-4 d-flex justify-content-center">
							{!imageData.data || loading ? (
								<div style={{ width: '100%' }}>
									<Skeleton height={'40vh'} data-tooltip-id="imageTrueColour" />
								</div>
							) : (
								<div
									style={{ width: '100%' }}
									// data-bs-toggle="modal"
									// data-bs-target="#compareImageModal"
									// onDoubleClick={() => setModalOpen(true)}
									data-tooltip-id="imageTrueColour"
								>
									<QCImageSmelter
										imageData={imageData.data}
										modalId={'id1'}
										imageUrl={imageData.data.data.true_color}
										openModal={modalOpen}
										showHotspots={true}
									/>
								</div>
							)}
							<Tooltip id="imageTrueColour" place="top" effect="solid">
								True colour
							</Tooltip>
						</div>
						<div className="col-12 my-2 my-lg-0 col-lg-4 d-flex justify-content-center">
							{!imageData.data || loading ? (
								<div style={{ width: '100%' }}>
									<Skeleton height={'40vh'} data-tooltip-id="imageRatio" />
								</div>
							) : (
								<div
									style={{ width: '100%' }}
									// data-bs-toggle="modal"
									// data-bs-target="#compareImageModal"
									// onDoubleClick={() => setModalOpen(true)}
									data-tooltip-id="imageRatio"
								>
									<QCImageSmelter
										modalId={'id2'}
										imageUrl={imageData.data.data.ratio}
										imageData={imageData.data}
										openModal={true}
									/>
								</div>
							)}
							{currentCapture && currentCapture.sensor === 'sentinel-2-l1c' ? (
								<Tooltip id="imageRatio" place="top" effect="solid">
									B12/B11
								</Tooltip>
							) : (
								<Tooltip id="imageRatio" place="top" effect="solid">
									B07/B06
								</Tooltip>
							)}
						</div>
						<div className="col-12 my-2 my-lg-0 col-lg-4 d-flex justify-content-center">
							{!imageData.data || loading ? (
								<div style={{ width: '100%' }}>
									<Skeleton height={'40vh'} data-tooltip-id="imageComp" />
								</div>
							) : (
								<div
									style={{ width: '100%' }}
									// data-bs-toggle="modal"
									// data-bs-target="#compareImageModal"
									// onDoubleClick={() => setModalOpen(true)}

									data-tooltip-id="imageComp"
								>
									<QCImageSmelter
										modalId={'id3'}
										imageData={imageData.data}
										imageUrl={imageData.data.data.comp}
										openModal={true}
									/>
								</div>
							)}
							<Tooltip id="imageComp" place="top" effect="solid">
								Thermal Composite
							</Tooltip>
						</div>
					</div>

					{/* IMAGE ERRORS */}
					<div className="row">
						<div className="col">
							{imageData.error ? (
								<h6
									style={{ cursor: 'pointer' }}
									data-tooltip-id="imageErrorWarning"
									className="text-center text-warning mt-2"
									onClick={() => fetchImageData()}
								>
									<i className="fa-solid fa-arrows-rotate me-2"></i>True Colour
								</h6>
							) : (
								<h6 className="text-center mt-2">True Colour</h6>
							)}
							<Tooltip id="imageErrorWarning" place="top" effect="solid">
								Images not fetched successfully; click to re-fetch all images
							</Tooltip>
						</div>
						<div className="col">
							{imageData.error ? (
								<h6
									style={{ cursor: 'pointer' }}
									data-tooltip-id="imageErrorWarning"
									className="text-center text-warning mt-2"
									onClick={() => fetchImageData()}
								>
									<i className="fa-solid fa-arrows-rotate me-2"></i>
									{currentCapture && currentCapture.sensor === 'sentinel-2-l1c'
										? 'B12/B11'
										: 'B07/B06'}
								</h6>
							) : (
								<h6 className="text-center mt-2">
									{currentCapture && currentCapture.sensor === 'sentinel-2-l1c'
										? 'B12/B11'
										: 'B07/B06'}
								</h6>
							)}
							<Tooltip id="imageErrorWarning" place="top" effect="solid">
								Images not fetched successfully; click to re-fetch all images
							</Tooltip>

							{/* {currentCapture && currentCapture.sensor === 'sentinel-2-l1c' ? (
								<h6 className="text-center mt-2">B12/B11</h6>
							) : (
								<h6 className="text-center mt-2">B07/B06</h6>
							)} */}
						</div>
						<div className="col">
							{imageData.error ? (
								<h6
									style={{ cursor: 'pointer' }}
									data-tooltip-id="imageErrorWarning"
									className="text-center text-warning mt-2"
									onClick={() => fetchImageData()}
								>
									<i className="fa-solid fa-arrows-rotate me-2"></i>Thermal
									Composite
								</h6>
							) : (
								<h6 className="text-center mt-2">Thermal Composite</h6>
							)}
							<Tooltip id="imageErrorWarning" place="top" effect="solid">
								Images not fetched successfully; click to re-fetch all images
							</Tooltip>
						</div>
					</div>

					{/* BARPLOT - HOTSPOTS - LAST SENSOR STATE - QC FORM */}
					<div
						className="row mt-1"
						id="QCing-barplot-hotspots-form-row"
						style={{ minHeight: '450px' }}
					>
						<div className="col-12 order-3 col-lg-12 order-lg-3 col-xxl-5 order-xxl-1 mt-3">
							<Barplot
								imageData={imageData.data ? true : false}
								plotData={historicPlotData.data}
								captureDate={currentCapture ? currentCapture.captured : null}
								plotDataError={historicPlotData.error}
								plotDataLoading={historicPlotData.loading}
								fetchBarplotData={fetchBarplotData}
								loading={loading}
							/>
						</div>
						<div className="col-12 order-2 col-lg-6 order-lg-2 col-xxl-3 order-xxl-2 mt-3">
							<HotspotsProfile
								imageData={imageData.data ? true : false}
								currentCapture={currentCapture}
								hotspotsProfile={hotspotsProfileData.data}
								fetchHotspotsData={fetchHotspotsData}
								hotspotsError={hotspotsProfileData.error}
								hotspotsLoading={hotspotsProfileData.loading}
								loading={loading}
							/>
						</div>
						<div className="col-12 order-1 col-lg-6 order-lg-1 col-xxl-4 order-xxl-3 mt-3">
							<QCingFormSmelter
								loading={loading}
								onQCSubmit={onQCSubmit}
								currentCap={currentCapture}
								imageData={imageData.data}
								userSelectedQCState={userSelectedQCState}
								qcState={qcState}
								selectedRadio={selectedRadio}
								setDisagreeReason={setDisagreeReason}
								disagreeReason={disagreeReason}
								userHasSelected={userHasSelected}
								skipCapture={skipCapture}
								backCapture={backCapture}
								imageDataError={imageData.error}
								imageDataLoading={imageData.loading}
								postCaptureStateSmelters={postCaptureStateSmelters}
								user={user}
							/>
						</div>
					</div>
				</div>
			</>
		);
	} else if (pipelineContextQCing.comm === 'stockpiles') {
		return (
			<>
				<div style={{ paddingX: '20px' }}>
					<hr></hr>
					<div className="row px-0 px-md-5 pb-2">
						<div className="col-12 col-md-4 d-flex justify-content-center">
							<p className="py-0 my-0" data-tooltip-id="QCingSiteInfo">
								<i class="fa-solid fa-location-dot me-2"></i>
								{!currentCapture ? (
									<Skeleton width={80} />
								) : (
									`${currentCapture.site_id} - ${currentCapture.site_name}`
								)}
							</p>
							<Tooltip id="QCingSiteInfo" place="top" effect="solid">
								Site id - Site name
							</Tooltip>
						</div>
						<div className="col-12 col-md-4 d-flex justify-content-center">
							<div className="row">
								<div className="d-flex justify-content-end">
									<p
										className="text-capitalize ps-4 py-0 my-0"
										data-tooltip-id="QCingCaptureInfo"
									>
										<i className="fa-regular fa-image  me-2"></i>
										{!currentCapture ? (
											<Skeleton width={80} />
										) : (
											`${currentCapture.id} - ${currentCapture.model_name}`
										)}
									</p>
									<Tooltip id="QCingCaptureInfo" place="top" effect="solid">
										Capture id - Sensor
									</Tooltip>
								</div>
							</div>
						</div>
						<div className="col-12 col-md-4 d-flex justify-content-center">
							<div className="row">
								<div className="d-flex justify-content-end">
									<p
										className="text-capitalize ps-4 py-0 my-0"
										data-tooltip-id="QCingCaptureDate"
									>
										<i class="fa-solid fa-calendar-day me-2"></i>
										{!currentCapture ? (
											<Skeleton width={80} />
										) : (
											`${formatDate(currentCapture.captured.slice(0, 10))}`
										)}
									</p>
									{/* <p className="ms-2">
										<a href={sentinelUrl} target="_blank">
											Sentinel Hub
										</a>
									</p> */}
									<Tooltip id="QCingCaptureDate" place="top" effect="solid">
										Capture date
									</Tooltip>
								</div>
							</div>
						</div>
					</div>

					<div className="row">
						<div
							className="col-12 py-3 py-md-1 col-lg-6"
							//style={{border: 'solid red 1px'}}
						>
							{!imageData.data ? (
								<div
									style={{
										width: '100%',
										height: '100%'
										//border: 'solid orange 1px'
									}}
								>
									<Skeleton height={'82vh'} data-tooltip-id="imageTrueColour" />
								</div>
							) : (
								<div
									style={{
										width: '100%',
										height: '85vh'
										//border: 'solid yellow 1px'
									}}
									//data-bs-toggle="modal"
									//data-bs-target="#id1"
									onClick={() => setModalOpen(true)}
									//data-tooltip-id="imageTrueColour"
								>
									<QCImageStockpiles
										imageData={imageData.data}
										modalId={'id4'}
										currentCapture={currentCapture}
										setDrawnItemsValid={setDrawnItemsValid}
										stockpilesGeojsons={stockpilesGeojsons}
										setStockpilesGeojsons={setStockpilesGeojsons}
										stockpilesCase={stockpilesCase}
										activeStockpile={activeStockpile}
										setActiveStockpile={setActiveStockpile}
										selectedMapView={selectedMapView}
									/>
								</div>
							)}
						</div>

						<div className="col-12 py-3 py-md-1 col-lg-6">
							<div
								className="row d-flex"
								style={{
									height: '40vh'
									//border: 'solid pink 1px'
								}}
							>
								<div className="col">
									<PixelCountPlot
										pixelPlotData={pixelPlotData.data}
										pixelPlotError={pixelPlotData.error}
										pixelPlotLoading={pixelPlotData.loading}
										sensorType={currentCapture?.sensor}
									/>
								</div>
							</div>
							<div
								style={{
									height: '43vh'
									// border: 'solid black 1px'
								}}
								className="row d-flex align-items-end"
							>
								{stockpilesCase === 2 && (
									<div className="col-7">
										<StockpilesGeojsonsList
											stockpilesGeojsons={stockpilesGeojsons}
											activeStockpile={activeStockpile}
											setActiveStockpile={setActiveStockpile}
											selectedMapView={selectedMapView}
											setSelectedMapView={setSelectedMapView}
										/>
									</div>
								)}
								<div
									className={
										stockpilesCase === 1
											? 'col pt-2 d-flex align-items-end justify-content-end '
											: 'col-5 order-1 pt-2 d-flex align-items-end justify-content-end '
									}
									style={{
										height: '100%'
										//border: 'solid green 1px'
									}}
								>
									<QCingFormStockpiles
										onStockpilesQCSubmit={onStockpilesQCSubmit}
										currentCapture={currentCapture}
										imageData={imageData.data}
										skipCapture={skipCapture}
										drawnItemsValid={drawnItemsValid}
										stockpilesFormValue={stockpilesFormValue}
										setStockpilesFormValue={setStockpilesFormValue}
										stockpilesCase={stockpilesCase}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</>
		);
	} else if (pipelineContextQCing.comm === 'aircraft') {
		return (
			<>
				{/* <div style={{ paddingX: '20px' }}>
					<hr></hr>
					<div className="row px-0 px-md-5 pb-2">
						<div className="col-12 col-md-4 d-flex justify-content-center">
							<p className="py-0 my-0" data-tooltip-id="QCingSiteInfo">
								<i class="fa-solid fa-location-dot me-2"></i>
								{!currentCapture ? (
									<Skeleton width={80} />
								) : (
									`${currentCapture.site_id} - ${currentCapture.site_name}`
								)}
							</p>
							<Tooltip id="QCingSiteInfo" place="top" effect="solid">
								Site id - Site name
							</Tooltip>
						</div>
						<div className="col-12 col-md-4 d-flex justify-content-center">
							<Row>
								<div className="d-flex justify-content-end">
									<p
										className="text-capitalize ps-4 py-0 my-0"
										data-tooltip-id="QCingCaptureInfo"
									>
										<i className="fa-regular fa-image  me-2"></i>
										{!currentCapture ? (
											<Skeleton width={80} />
										) : (
											`${currentCapture.id} - ${currentCapture.model_name}`
										)}
									</p>
									<Tooltip id="QCingCaptureInfo" place="top" effect="solid">
										Capture id - Sensor
									</Tooltip>
								</div>
							</Row>
						</div>
						<div className="col-12 col-md-4 d-flex justify-content-center">
							<Row>
								<div className="d-flex justify-content-end">
									<p
										className="text-capitalize ps-4 py-0 my-0"
										data-tooltip-id="QCingCaptureDate"
									>
										<i class="fa-solid fa-calendar-day me-2"></i>
										{!currentCapture ? (
											<Skeleton width={80} />
										) : (
											`${formatDate(currentCapture.captured.slice(0, 10))}`
										)}
									</p>
									{/* <p className="ms-2">
										<a href={sentinelUrl} target="_blank">
											Sentinel Hub
										</a>
									</p> 
									<Tooltip id="QCingCaptureDate" place="top" effect="solid">
										Capture date
									</Tooltip>
								</div>
							</Row>
						</div>
					</div>
					<div className="row">
						<div
							className="col-12 py-3 py-md-1 col-lg-6"
							//style={{border: 'solid red 1px'}}
						>
							{!imageData.data ? (
								<div
									style={{
										width: '100%',
										height: '100%'
										//border: 'solid orange 1px'
									}}
								>
									<Skeleton height={'82vh'} data-tooltip-id="imageTrueColour" />
								</div>
							) : (
								<div
									style={{
										width: '100%',
										height: '85vh'
										//border: 'solid yellow 1px'
									}}
									//data-bs-toggle="modal"
									//data-bs-target="#id1"
									onClick={() => setModalOpen(true)}
									//data-tooltip-id="imageTrueColour"
								>
									<QCImage
										modalId={'id4'}
										stockpilesGeojsons={stockpilesGeojsons}
										setStockpilesGeojsons={setStockpilesGeojsons}
										imageUrl={imageData.data.true_color}
										geojson={imageData.data.aoi}
										perm_regions={imageData.data.perm_regions}
										hotspots={imageData.data.hotspots}
										bbox={imageData.data.bbox}
										imageData={imageData.data}
										setEditedGeojson={setEditedGeojson}
										openModal={modalOpen}
										featureCollection={featureCollection}
										setFeatureCollection={setFeatureCollection}
										featureLayers={featureLayers}
										setFeatureLayers={setFeatureLayers}
										currentCapture={currentCapture}
										setDrawnItemsValid={setDrawnItemsValid}
										drawnItemsValid={drawnItemsValid}
										stockpilesCase={stockpilesCase}
										activeStockpile={activeStockpile}
										setActiveStockpile={setActiveStockpile}
										selectedMapView={selectedMapView}
										setSelectedMapView={setSelectedMapView}
									/>
								</div>
							)}
						</div>
						<div className="col-12 py-3 py-md-1 col-lg-6">
							<div
								className="row d-flex"
								style={{
									height: '40vh'
									//border: 'solid pink 1px'
								}}
							>
								<div className="col">
									<PixelCountPlot
										pixelPlotData={pixelPlotData.data}
										pixelPlotError={pixelPlotData.error}
										pixelPlotLoading={pixelPlotData.loading}
									/>
								</div>
							</div>
							<div
								style={{ height: '43vh',
								// border: 'solid black 1px'
							 }}
								className="row d-flex align-items-end"
							>
								{stockpilesCase === 2 && (
									<div className="col-7">
										<StockpilesGeojsonsList
											stockpilesGeojsons={stockpilesGeojsons}
											activeStockpile={activeStockpile}
											setActiveStockpile={setActiveStockpile}
											selectedMapView={selectedMapView}
											setSelectedMapView={setSelectedMapView}
										/>
									</div>
								)}
								<div
									className={
										stockpilesCase === 1
											? 'col pt-2 d-flex align-items-end justify-content-end '
											: 'col-5 order-1 pt-2 d-flex align-items-end justify-content-end '
									}
									style={{ height: '100%',
									 //border: 'solid green 1px'
									 }}
								>
									<QCingFormStockpiles
										onStockpilesQCSubmit={onStockpilesQCSubmit}
										currentCapture={currentCapture}
										imageData={imageData.data}
										skipCapture={skipCapture}
										drawnItemsValid={drawnItemsValid}
										stockpilesFormValue={stockpilesFormValue}
										setStockpilesFormValue={setStockpilesFormValue}
										stockpilesCase={stockpilesCase}
									/>
								</div>
							</div>
						</div>
					</div>
				</div> */}
			</>
		);
	}
};

// QCing.propTypes = {
// 	currentCapture: PropTypes.object,
// 	currentCaptureIndex: PropTypes.number,
// 	qcState: PropTypes.number,
// 	setQCState: PropTypes.func,
// 	onQCSubmit,
// 	onStockpilesQCSubmit,
// 	setDisagreeReason,
// 	stockpilesFormValue,
// 	setStockpilesFormValue,
// 	loading,
// 	setLoading,
// 	postCaptureStateSmelters

// 	// name: PropTypes.string.isRequired,
// 	// age: PropTypes.number.isRequired,
// 	// isAdmin: PropTypes.bool,
// 	// tags: PropTypes.arrayOf(PropTypes.string),
// 	// onClick: PropTypes.func,
//   };

//   QCing.defaultProps = {
// 	isAdmin: false,
// 	tags: [],
// 	onClick: () => {},
//   };

export default QCing;
